import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { broadcastSettingsUpdate } from '@/hooks/useSiteSettings';
import RichTextEditor from '@/components/base/RichTextEditor';

type SettingsMap = Record<string, string>;

const DEFAULTS: SettingsMap = {
  site_name: 'API MART',
  site_description: '',
  meta_title: '',
  meta_keywords: '',
  meta_description: '',
  og_title: '',
  og_description: '',
  og_image: '',
  timezone: 'UTC',
  date_format: 'MM/DD/YYYY',
  time_format: '12h',
  default_language: 'en',
  company_name: '',
  company_email: '',
  admin_notification_email: '',
  privacy_policy_title: 'Privacy Policy',
  privacy_policy_content: '',
  terms_title: 'Terms and Conditions',
  terms_content: '',
  site_logo_url: '',
  site_favicon_url: '',
};

type SectionKey = 'seo' | 'system' | 'branding' | 'privacy' | 'terms';

const HASH_TO_SECTION: Record<string, SectionKey> = {
  '#seo': 'seo',
  '#system': 'system',
  '#branding': 'branding',
  '#privacy': 'privacy',
  '#terms': 'terms',
};

const SECTION_TO_HASH: Record<SectionKey, string> = {
  seo: '#seo',
  system: '#system',
  branding: '#branding',
  privacy: '#privacy',
  terms: '#terms',
};

export default function SiteSettingsTab() {
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState<SettingsMap>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<SectionKey | null>(null);
  const [msgs, setMsgs] = useState<Partial<Record<SectionKey, { type: 'success' | 'error'; text: string }>>>({});

  // Derive active section from URL hash
  const activeSection: SectionKey = HASH_TO_SECTION[location.hash] ?? 'seo';

  const setActiveSection = (key: SectionKey) => {
    navigate(`/admin/site-settings${SECTION_TO_HASH[key]}`, { replace: false });
  };
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('site_settings').select('key, value');
    if (data) {
      const map: SettingsMap = { ...DEFAULTS };
      data.forEach((row: { key: string; value: string }) => { map[row.key] = row.value ?? ''; });
      setSettings(map);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const set = (key: string, value: string) => setSettings(prev => ({ ...prev, [key]: value }));

  const uploadFile = async (file: File, type: 'logo' | 'favicon') => {
    const setter = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
    const key = type === 'logo' ? 'site_logo_url' : 'site_favicon_url';
    setter(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${type}-${Date.now()}.${ext}`;
      const { data, error } = await supabase.storage
        .from('site-assets')
        .upload(fileName, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from('site-assets').getPublicUrl(data.path);
      const newUrl = urlData.publicUrl;
      // Update local state
      set(key, newUrl);
      // Immediately persist to DB so it survives refresh
      await supabase
        .from('site_settings')
        .upsert({ key, value: newUrl, updated_at: new Date().toISOString() }, { onConflict: 'key' });
      // Broadcast live update
      broadcastSettingsUpdate({ [key]: newUrl });
      if (type === 'favicon') applyFaviconLive(newUrl);
    } catch (err) {
      console.error('Upload failed', err);
    }
    setter(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'favicon') => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file, type);
  };

  const applyFaviconLive = (url: string) => {
    ['link[rel="icon"]', 'link[rel="shortcut icon"]'].forEach(sel => {
      let el = document.querySelector(sel) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', sel.includes('shortcut') ? 'shortcut icon' : 'icon');
        document.head.appendChild(el);
      }
      el.setAttribute('href', url);
    });
  };

  const saveSection = async (section: SectionKey, keys: string[]) => {
    setSaving(section);
    setMsgs(prev => ({ ...prev, [section]: undefined }));
    try {
      // Use upsert with ignoreDuplicates:false to properly handle both insert and update
      await supabase.from('site_settings').upsert(
        keys.map(key => ({ key, value: settings[key] ?? '', updated_at: new Date().toISOString() })),
        { onConflict: 'key', ignoreDuplicates: false }
      );

      // Build the partial update map for the saved keys
      const updatedPartial: Record<string, string> = {};
      keys.forEach(k => { updatedPartial[k] = settings[k] ?? ''; });

      // Broadcast to all useSiteSettings consumers (Sidebar logo, etc.) immediately
      broadcastSettingsUpdate(updatedPartial);

      // Live-apply favicon immediately after saving branding
      if (section === 'branding' && settings.site_favicon_url) {
        applyFaviconLive(settings.site_favicon_url);
      }
      setMsgs(prev => ({ ...prev, [section]: { type: 'success', text: 'Settings saved successfully!' } }));
    } catch {
      setMsgs(prev => ({ ...prev, [section]: { type: 'error', text: 'Failed to save. Please try again.' } }));
    }
    setSaving(null);
    setTimeout(() => setMsgs(prev => ({ ...prev, [section]: undefined })), 4000);
  };

  const sections: { key: SectionKey; label: string; icon: string }[] = [
    { key: 'seo', label: 'SEO & Open Graph', icon: 'ri-search-eye-line' },
    { key: 'system', label: 'System Core Settings', icon: 'ri-settings-3-line' },
    { key: 'branding', label: 'Favicon & Logo', icon: 'ri-image-line' },
    { key: 'privacy', label: 'Privacy Policy', icon: 'ri-shield-user-line' },
    { key: 'terms', label: 'Terms & Conditions', icon: 'ri-file-text-line' },
  ];

  if (loading) {
    return <div className="space-y-4 animate-pulse">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-xl" />)}</div>;
  }

  const SaveBtn = ({ section, keys }: { section: SectionKey; keys: string[] }) => (
    <div className="space-y-3">
      <AnimatePresence>
        {msgs[section] && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs border ${msgs[section]!.type === 'success' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-red-50 text-red-600 border-red-100'}`}>
            <i className={msgs[section]!.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
            {msgs[section]!.text}
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => saveSection(section, keys)} disabled={saving === section}
        className="flex items-center gap-2 px-5 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors">
        {saving === section ? <><i className="ri-loader-4-line animate-spin" />Saving…</> : <><i className="ri-save-line" />Save Changes</>}
      </button>
    </div>
  );

  return (
    <div className="flex gap-6">
      {/* Sidebar nav */}
      <div className="w-56 shrink-0 space-y-1">
        {sections.map(s => (
          <button key={s.key} onClick={() => setActiveSection(s.key)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap transition-colors text-left ${activeSection === s.key ? 'bg-sidebar text-white font-semibold' : 'text-gray-600 hover:bg-gray-100'}`}>
            <i className={`${s.icon} text-base shrink-0`} />{s.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* SEO & OG */}
        {activeSection === 'seo' && (
          <div className="bg-white rounded-2xl p-6 space-y-5">
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-0.5">SEO Settings</h3>
              <p className="text-xs text-gray-400">Update your meta tags for better search engine visibility</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Meta Title</label>
                <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  placeholder="API MART — Buy & Sell APIs | Developer Marketplace"
                  value={settings.meta_title} onChange={e => set('meta_title', e.target.value)} maxLength={60} />
                <div className="text-[11px] text-gray-400 mt-0.5 text-right">{settings.meta_title?.length ?? 0}/60</div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Meta Keywords</label>
                <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  placeholder="API marketplace, buy APIs, developer tools"
                  value={settings.meta_keywords} onChange={e => set('meta_keywords', e.target.value)} />
                <p className="text-[11px] text-gray-400 mt-0.5">Separate keywords with commas</p>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Meta Description</label>
                <textarea className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                  rows={3} placeholder="120–160 characters describing your site…"
                  value={settings.meta_description} onChange={e => { if (e.target.value.length <= 160) set('meta_description', e.target.value); }} maxLength={160} />
                <div className="text-[11px] text-gray-400 text-right mt-0.5">{settings.meta_description?.length ?? 0}/160</div>
              </div>
            </div>

            <div className="border-t border-surface-3 pt-5">
              <h3 className="font-bold text-gray-900 text-base mb-0.5">Open Graph Settings</h3>
              <p className="text-xs text-gray-400 mb-4">Controls how your site appears when shared on social media</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">OG Title</label>
                  <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="API MART — The Developer API Marketplace"
                    value={settings.og_title} onChange={e => set('og_title', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">OG Description</label>
                  <textarea className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                    rows={3} placeholder="Discover and integrate production-ready APIs…"
                    value={settings.og_description} onChange={e => { if (e.target.value.length <= 500) set('og_description', e.target.value); }} maxLength={500} />
                  <div className="text-[11px] text-gray-400 text-right mt-0.5">{settings.og_description?.length ?? 0}/500</div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">OG Image URL</label>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1">
                      <input
                        className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                        placeholder="https://your-domain.com/og-image.png"
                        value={settings.og_image}
                        onChange={e => set('og_image', e.target.value)}
                      />
                      <p className="text-[11px] text-gray-400 mt-1">Recommended: 1200×630px PNG or JPG. Shown when your site is shared on Twitter, LinkedIn, Slack, etc.</p>
                    </div>
                    {settings.og_image && (
                      <div className="w-24 h-14 rounded-lg border border-surface-3 overflow-hidden shrink-0 bg-gray-50">
                        <img src={settings.og_image} alt="OG preview" className="w-full h-full object-cover" onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <SaveBtn section="seo" keys={['meta_title', 'meta_keywords', 'meta_description', 'og_title', 'og_description', 'og_image']} />
          </div>
        )}

        {/* System Core */}
        {activeSection === 'system' && (
          <div className="bg-white rounded-2xl p-6 space-y-5">
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-0.5">System Core Settings</h3>
              <p className="text-xs text-gray-400">Configure fundamental system-wide parameters</p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Site Name</label>
                  <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="API MART" value={settings.site_name} onChange={e => set('site_name', e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Company Name</label>
                  <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="API MART Inc." value={settings.company_name} onChange={e => set('company_name', e.target.value)} />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Site Description</label>
                <textarea className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                  rows={2} placeholder="Brief description of your platform…"
                  value={settings.site_description} onChange={e => { if (e.target.value.length <= 300) set('site_description', e.target.value); }} maxLength={300} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Company Email</label>
                <input type="email" className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  placeholder="support@apimarts.com" value={settings.company_email} onChange={e => set('company_email', e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">
                  Admin Notification Email
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold bg-amber-50 text-amber-600 border border-amber-200 px-2 py-0.5 rounded-full">
                    <i className="ri-notification-3-line text-[9px]" />New Ticket Alerts
                  </span>
                </label>
                <input type="email" className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                  placeholder="your-admin@gmail.com" value={settings.admin_notification_email} onChange={e => set('admin_notification_email', e.target.value)} />
                <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                  <i className="ri-information-line" />This is where new ticket notifications are sent. Set it to your personal inbox so you never miss a ticket.
                </p>
              </div>
            </div>

            <div className="border-t border-surface-3 pt-5">
              <h4 className="font-semibold text-gray-800 text-sm mb-3">Localization</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Timezone</label>
                  <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
                    value={settings.timezone} onChange={e => set('timezone', e.target.value)}>
                    {['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Sydney'].map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Default Language</label>
                  <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
                    value={settings.default_language} onChange={e => set('default_language', e.target.value)}>
                    <option value="en">English</option>
                    <option value="fr">French</option>
                    <option value="es">Spanish</option>
                    <option value="de">German</option>
                    <option value="ar">Arabic</option>
                    <option value="zh">Chinese</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Date Format</label>
                  <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
                    value={settings.date_format} onChange={e => set('date_format', e.target.value)}>
                    <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Time Format</label>
                  <select className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
                    value={settings.time_format} onChange={e => set('time_format', e.target.value)}>
                    <option value="12h">12-hour (AM/PM)</option>
                    <option value="24h">24-hour</option>
                  </select>
                </div>
              </div>
            </div>
            <SaveBtn section="system" keys={['site_name', 'site_description', 'company_name', 'company_email', 'admin_notification_email', 'timezone', 'date_format', 'time_format', 'default_language']} />
          </div>
        )}

        {/* Branding */}
        {activeSection === 'branding' && (
          <div className="bg-white rounded-2xl p-6 space-y-6">
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-0.5">Favicon &amp; Site Logo</h3>
              <p className="text-xs text-gray-400">Upload your brand assets or paste a direct URL. Changes take effect after saving.</p>
            </div>

            {/* Site Logo */}
            <div className="border border-surface-3 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-image-2-line text-base text-brand-500" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">Site Logo</h4>
                  <p className="text-[11px] text-gray-400">Recommended: PNG or SVG, min 200×60px, transparent background</p>
                </div>
              </div>

              {/* Preview */}
              <div className="flex items-center gap-4">
                <div className="w-40 h-16 border border-dashed border-surface-3 rounded-lg bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                  {settings.site_logo_url ? (
                    <img src={settings.site_logo_url} alt="Site logo preview" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <div className="text-center">
                      <i className="ri-image-line text-xl text-gray-300" />
                      <p className="text-[10px] text-gray-300 mt-0.5">No logo</p>
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="https://your-cdn.com/logo.png"
                    value={settings.site_logo_url}
                    onChange={e => set('site_logo_url', e.target.value)}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">or</span>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml,image/*"
                      className="hidden"
                      onChange={e => handleFileChange(e, 'logo')}
                    />
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-surface-3 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                    >
                      {uploadingLogo ? <><i className="ri-loader-4-line animate-spin" />Uploading…</> : <><i className="ri-upload-2-line" />Upload File</>}
                    </button>
                    {settings.site_logo_url && (
                      <button onClick={() => set('site_logo_url', '')} className="text-xs text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap transition-colors">
                        <i className="ri-delete-bin-line mr-0.5" />Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Favicon */}
            <div className="border border-surface-3 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-global-line text-base text-brand-500" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">Favicon</h4>
                  <p className="text-[11px] text-gray-400">Recommended: ICO, PNG or SVG, 32×32px or 64×64px square</p>
                </div>
              </div>

              {/* Preview */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 border border-dashed border-surface-3 rounded-xl bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
                  {settings.site_favicon_url ? (
                    <img src={settings.site_favicon_url} alt="Favicon preview" className="w-10 h-10 object-contain" />
                  ) : (
                    <div className="text-center">
                      <i className="ri-global-line text-xl text-gray-300" />
                      <p className="text-[10px] text-gray-300 mt-0.5">None</p>
                    </div>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="text"
                    className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                    placeholder="https://your-cdn.com/favicon.ico"
                    value={settings.site_favicon_url}
                    onChange={e => set('site_favicon_url', e.target.value)}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">or</span>
                    <input
                      ref={faviconInputRef}
                      type="file"
                      accept="image/x-icon,image/png,image/jpeg,image/jpg,image/svg+xml,image/*"
                      className="hidden"
                      onChange={e => handleFileChange(e, 'favicon')}
                    />
                    <button
                      onClick={() => faviconInputRef.current?.click()}
                      disabled={uploadingFavicon}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-surface-3 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                    >
                      {uploadingFavicon ? <><i className="ri-loader-4-line animate-spin" />Uploading…</> : <><i className="ri-upload-2-line" />Upload File</>}
                    </button>
                    {settings.site_favicon_url && (
                      <button onClick={() => set('site_favicon_url', '')} className="text-xs text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap transition-colors">
                        <i className="ri-delete-bin-line mr-0.5" />Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-start gap-2.5">
              <i className="ri-information-line text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">After saving, both the favicon and site logo update instantly across the entire platform — no reload needed.</p>
            </div>

            <SaveBtn section="branding" keys={['site_logo_url', 'site_favicon_url']} />
          </div>
        )}

        {/* Privacy Policy */}
        {activeSection === 'privacy' && (
          <div className="bg-white rounded-2xl p-6 space-y-4">
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-0.5">Privacy Policy</h3>
              <p className="text-xs text-gray-400">Set up your privacy policy page visible to users</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Title</label>
              <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                placeholder="Privacy Policy" value={settings.privacy_policy_title} onChange={e => set('privacy_policy_title', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Content</label>
              <RichTextEditor
                value={settings.privacy_policy_content}
                onChange={html => set('privacy_policy_content', html)}
                placeholder="Enter your full privacy policy here — use headings, bullet points and bold text to structure it clearly…"
                minHeight={360}
              />
              <div className="text-[11px] text-gray-400 text-right mt-0.5">{settings.privacy_policy_content?.length ?? 0}/50,000 chars</div>
            </div>
            <SaveBtn section="privacy" keys={['privacy_policy_title', 'privacy_policy_content']} />
          </div>
        )}

        {/* Terms */}
        {activeSection === 'terms' && (
          <div className="bg-white rounded-2xl p-6 space-y-4">
            <div>
              <h3 className="font-bold text-gray-900 text-base mb-0.5">Terms &amp; Conditions</h3>
              <p className="text-xs text-gray-400">Set up your terms and conditions page visible to users</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Title</label>
              <input className="w-full text-sm border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                placeholder="Terms and Conditions" value={settings.terms_title} onChange={e => set('terms_title', e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Content</label>
              <RichTextEditor
                value={settings.terms_content}
                onChange={html => set('terms_content', html)}
                placeholder="Enter your full terms and conditions here — use headings, bullet points and bold text to structure it clearly…"
                minHeight={360}
              />
              <div className="text-[11px] text-gray-400 text-right mt-0.5">{settings.terms_content?.length ?? 0}/50,000 chars</div>
            </div>
            <SaveBtn section="terms" keys={['terms_title', 'terms_content']} />
          </div>
        )}
      </div>
    </div>
  );
}
